import { AdminLayout } from "@/components/admin-layout";
import {
	PageContentContainer,
	PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { Badge } from "@fp/ui/components/base/badges/badges";
import { AddressMap, formatAddress } from "@fp/ui/components/common/address";
import { formatLocalDate } from "@fp/ui/components/common/local-date-time";
import { Link } from "@fp/ui/components/base/link/link";
import {
	CheckCircle,
	Send01,
	RefreshCcw01,
	MarkerPin01,
	User01,
	ChevronRight,
} from "@untitledui/icons";

import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useDataRefresh } from "@/hooks/useSignalR";
import { PayoutQueueNav } from "@/components/payout-queue-nav";
import { InvoiceExportButton } from "@/components/invoice-export-button";
import { invoiceStatusLabels } from "@/lib/invoice-utils";
import { PayoutRequestActions } from "@/components/payout-request-actions";
import {
	PayoutShortcutsHelp,
	payoutRequestShortcuts,
} from "@/components/payout-shortcuts-help";
import { GpsVerificationSection } from "@/components/gps-verification-section";
import { PhotoGallery } from "@/components/photo-gallery";
import { usePayoutReviewEscape } from "@/hooks/usePayoutReviewEscape";
import { isOverlayOpen } from "@/lib/is-overlay-open";
import { usePayoutReviewQueue } from "@/lib/payout-review-queue";
import {
	getPayoutRequestEvidence,
} from "@fp/admin-api-client/payout-requests/payout-requests.client";
import {
	getGetClaimTimelineDetailQueryOptions,
	getGetClaimTimelineQueryOptions,
	useGetClaimTimeline,
	useGetClaimTimelineDetail,
} from "@fp/admin-api-client/opportunity-claims/opportunity-claims.client";
import {
	getGetOpportunityByIdQueryKey,
	getGetOpportunityByIdQueryOptions,
} from "@fp/admin-api-client/opportunities/opportunities.client";
import { getGetOrganizationSettingsQueryOptions } from "@fp/admin-api-client/organizations/organizations.client";
import type { PayoutRequestDto } from "@fp/admin-api-client/types/payoutRequestDto";
import type { OpportunityClaimDto } from "@fp/admin-api-client/types/opportunityClaimDto";
import type { OpportunityDto } from "@fp/admin-api-client/types/opportunityDto";
import type { PayoutRequestEvidenceDto } from "@fp/admin-api-client/types/payoutRequestEvidenceDto";
import type { OpportunityClaimTimelineEntryDto } from "@fp/admin-api-client/types/opportunityClaimTimelineEntryDto";
import { DataRefreshEvent, PayRateType } from "@fp/admin-api-client";
import type { TimelinePayoutRequestDto } from "@fp/admin-api-client/types/timelinePayoutRequestDto";

export const Route = createFileRoute("/opportunity-claims/$claimId/timeline/")({
	component: RouteComponent,
	loader: async ({ context: { queryClient }, params }) => {
		// Fetch timeline detail by claim ID - returns current payout request and history
		const claimDetail = await queryClient.fetchQuery(
			getGetClaimTimelineDetailQueryOptions(params.claimId),
		);

		// Fetch timeline entries for this claim
		const timeline = await queryClient.fetchQuery(
			getGetClaimTimelineQueryOptions(params.claimId),
		);

		const opportunity = await queryClient.fetchQuery(
			getGetOpportunityByIdQueryOptions(claimDetail.data.claim.opportunityId),
		);

		const orgSettings = await queryClient.fetchQuery(
			getGetOrganizationSettingsQueryOptions(),
		);

		// Use backend-persisted geocoded coordinates
		let addressCoords: [number, number] | null = null;
		if (opportunity.data.address) {
			const { latitude, longitude } = opportunity.data.address;
			if (latitude != null && longitude != null) {
				addressCoords = [latitude, longitude];
			}
		}

		return {
			payoutRequest: claimDetail.data.currentPayoutRequest,
			claim: claimDetail.data.claim,
			timelineEntries: timeline.data,
			payoutRequestHistory: claimDetail.data.payoutRequestHistory,
			opportunity: opportunity.data,
			proximityThresholdFeet: orgSettings.data.gpsProximityThresholdFeet,
			addressCoords,
		};
	},
	pendingComponent: () => <PageLoading />,
});

type TimelineEventType =
	| "claimed"
	| "gps_captured"
	| "gps_deleted"
	| "submitted"
	| "returned"
	| "approved"
	| "denied"
	| "paid"
	| "failed";


interface TimelineEvent {
	id: string;
	type: TimelineEventType;
	date: Date;
	actorName: string;
	payoutRequestId?: string;
	data?: {
		payoutRequest?: TimelinePayoutRequestDto;
		evidence?: PayoutRequestEvidenceDto;
		claim?: OpportunityClaimDto;
		opportunity?: OpportunityDto;
	};
}

function mapServerEventToType(eventName: string): TimelineEventType | null {
	switch (eventName) {
		case "opportunity-claimed":
			return "claimed";
		case "gps-captured":
			return "gps_captured";
		case "gps-checkin-deleted":
			return "gps_deleted";
		case "payout-request-submitted":
			return "submitted";
		case "payout-request-returned":
			return "returned";
		case "payout-request-approved":
			return "approved";
		case "payout-request-denied":
			return "denied";
		case "payout-request-paid":
			return "paid";
		case "payout-request-failed":
			return "failed";
		default:
			return null;
	}
}

function buildTimelineEvents(
	claim: OpportunityClaimDto,
	timelineEntries: OpportunityClaimTimelineEntryDto[],
	currentPayoutRequest: PayoutRequestDto | null | undefined,
	payoutRequestHistory: TimelinePayoutRequestDto[],
	opportunity: OpportunityDto,
): TimelineEvent[] {
	const events: TimelineEvent[] = [];

	// Build a map of payoutRequestId -> basic payout request info
	const payoutRequestMap = new Map<string, TimelinePayoutRequestDto>();
	for (const pr of payoutRequestHistory) {
		payoutRequestMap.set(pr.id, pr);
	}

	// Check if server timeline has a "claimed" event
	const hasServerClaimedEvent = timelineEntries.some(
		(e) => e.eventName === "opportunity-claimed",
	);

	// Fallback: Add "claimed" event from claim data if not in server timeline
	// This handles claims created before the timeline consumer was deployed
	if (!hasServerClaimedEvent) {
		events.push({
			id: `fallback-claimed-${claim.id}`,
			type: "claimed",
			date: new Date(claim.claimDate),
			actorName: claim.claimerName,
		});
	}

	// Convert server timeline entries to UI events
	for (const entry of timelineEntries) {
		const eventType = mapServerEventToType(entry.eventName);
		if (!eventType) continue;

		const event: TimelineEvent = {
			id: entry.id,
			type: eventType,
			date: new Date(entry.occurredAt),
			actorName: entry.actorName ?? "System",
			payoutRequestId: entry.payoutRequestId ?? undefined,
		};

		// Attach data for any submission event (for expanded view)
		if (eventType === "submitted" && entry.payoutRequestId) {
			const isCurrentRequest =
				entry.payoutRequestId === currentPayoutRequest?.id;
			const prData = payoutRequestMap.get(entry.payoutRequestId);

			if (prData) {
				event.data = {
					payoutRequest: prData,
					// Only include evidence for current request (others are lazy-loaded)
					evidence: isCurrentRequest
						? (currentPayoutRequest?.evidence ?? undefined)
						: undefined,
					claim: claim,
					opportunity: opportunity,
				};
			}
		}

		events.push(event);
	}

	// Fallback: Add "submitted" events for payout requests without a timeline entry
	// This handles payout requests created before the timeline consumer was deployed
	const submittedPayoutRequestIds = new Set(
		events
			.filter((e) => e.type === "submitted" && e.payoutRequestId)
			.map((e) => e.payoutRequestId),
	);

	for (const pr of payoutRequestHistory) {
		if (submittedPayoutRequestIds.has(pr.id)) continue;

		const isCurrentRequest = pr.id === currentPayoutRequest?.id;
		events.push({
			id: `fallback-submitted-${pr.id}`,
			type: "submitted",
			date: new Date(pr.createdDate),
			actorName: pr.payeeName,
			payoutRequestId: pr.id,
			data: {
				payoutRequest: pr,
				evidence: isCurrentRequest
					? (currentPayoutRequest?.evidence ?? undefined)
					: undefined,
				claim: claim,
				opportunity: opportunity,
			},
		});
	}

	// Sort by date descending (most recent first)
	events.sort((a, b) => b.date.getTime() - a.date.getTime());

	return events;
}

function getEventIcon(type: TimelineEventType) {
	switch (type) {
		case "claimed":
			return <User01 className="h-4 w-4" />;
		case "gps_captured":
			return <MarkerPin01 className="h-4 w-4" />;
		case "gps_deleted":
			return <MarkerPin01 className="h-4 w-4 text-red-500" />;
		case "submitted":
			return <Send01 className="h-4 w-4" />;
		case "returned":
			return <RefreshCcw01 className="h-4 w-4 text-warning-500" />;
		case "approved":
			return <CheckCircle className="h-4 w-4 text-green-600" />;
		case "denied":
			return <CheckCircle className="h-4 w-4 text-red-600" />;
		case "paid":
			return <CheckCircle className="h-4 w-4 text-green-600" />;
		case "failed":
			return <CheckCircle className="h-4 w-4 text-red-600" />;
	}
}

function getEventLabel(type: TimelineEventType, actorName: string): string {
	switch (type) {
		case "claimed":
			return `Claimed by ${actorName}`;
		case "gps_captured":
			return "GPS Coordinates Captured";
		case "gps_deleted":
			return "GPS Check-in Deleted";
		case "submitted":
			return `Submitted by ${actorName}`;
		case "returned":
			return `Returned by ${actorName}`;
		case "approved":
			return `Approved by ${actorName}`;
		case "denied":
			return `Denied by ${actorName}`;
		case "paid":
			return "Payment Processed";
		case "failed":
			return "Payment Failed";
	}
}

function CollapsedTimelineEvent({
	event,
	showChevron = false,
	onClick,
}: {
	event: TimelineEvent;
	showChevron?: boolean;
	onClick?: () => void;
}) {
	const isClickable = showChevron && onClick;
	return (
		<button
			type="button"
			className={`w-full flex items-center justify-between py-3 ${isClickable ? "cursor-pointer" : ""}`}
			onClick={isClickable ? onClick : undefined}
			onKeyDown={
				isClickable ? (e) => e.key === "Enter" && onClick() : undefined
			}
			role={isClickable ? "button" : undefined}
			tabIndex={isClickable ? 0 : undefined}
		>
			<div className="flex items-center gap-2 text-sm text-gray-700">
				{getEventIcon(event.type)}
				<span>{getEventLabel(event.type, event.actorName)}</span>
			</div>
			<div className="flex items-center gap-1 text-sm text-gray-500">
				<span>{formatLocalDate(event.date, "time")}</span>
				{showChevron && <ChevronRight className="h-4 w-4" />}
			</div>
		</button>
	);
}

function getRateTypeDisplay(payRateType?: PayRateType): string {
	if (!payRateType) return "Flat Rate";
	switch (payRateType) {
		case PayRateType.FlatRate:
			return "Flat Rate";
		case PayRateType.Hourly:
			return "Per Hour";
		case PayRateType.PerUnit:
			return "Per Unit";
		case PayRateType.Percentage:
			return "Percentage";
		default:
			return "Unknown Rate";
	}
}

function ExpandedSubmissionEvent({
	event,
	onCollapse,
	lazyEvidence,
	isLoading,
	addressCoords,
	proximityThresholdFeet,
	isOverlayOpen = false,
	isExpanded = false,
}: {
	event: TimelineEvent;
	onCollapse?: () => void;
	lazyEvidence?: PayoutRequestEvidenceDto;
	isLoading?: boolean;
	addressCoords: [number, number] | null;
	proximityThresholdFeet: number;
	isOverlayOpen?: boolean;
	isExpanded: boolean;
}) {
	const payoutRequest = event.data?.payoutRequest;
	// Use lazy-loaded evidence if available, otherwise use pre-loaded evidence
	const evidence = lazyEvidence ?? event.data?.evidence;

	if (!payoutRequest) return null;

	const gpsCheckins = evidence?.gpsCheckins ?? [];
	const documents = evidence?.documents ?? [];

	return (
		<div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
			{/* Collapsible header */}
			{onCollapse && (
				<button
					className={`w-full flex items-center justify-between cursor-pointer ${isExpanded ? "pb-2 mb-2 border-b border-gray-100" : ""}`}
					onClick={onCollapse}
					onKeyDown={(e) => e.key === "Enter" && onCollapse()}
					type="button"
					tabIndex={0}
				>
					<div className="flex items-center gap-2 text-sm text-gray-700">
						{getEventIcon(event.type)}
						<span>{getEventLabel(event.type, event.actorName)}</span>
					</div>
					<div className="flex items-center gap-1 text-sm text-gray-500">
						<span>{formatLocalDate(event.date, "time")}</span>
						<ChevronRight className="h-4 w-4 rotate-90" />
					</div>
				</button>
			)}
			{/* Header with name and time */}
			{isExpanded && (
				<div className="flex items-center justify-between mb-4">
					<div className="flex items-center gap-3">
						<h3 className="text-xl font-semibold text-gray-900">
							{payoutRequest.payeeName}
						</h3>
						{payoutRequest.invoice && (
							<span className="text-sm text-gray-500">Invoice {payoutRequest.invoice.number} · {invoiceStatusLabels[payoutRequest.invoice.status]}</span>
						)}
					</div>
					<div className="flex items-center gap-3">
						<InvoiceExportButton invoice={payoutRequest.invoice} />
						{!onCollapse && (
							<span className="text-sm text-gray-500">
								{formatLocalDate(event.date, "time")}
							</span>
						)}
					</div>
				</div>
			)}

			{/* Loading indicator */}
			{isLoading && (
				<div className="flex items-center justify-center py-8">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
				</div>
			)}

			{/* Content (hidden when loading) */}
			{!isLoading && isExpanded && (
				<>
					{/* Payout info */}
					<div className=" pb-4 mb-4">
						{event.data?.opportunity?.payRate?.type === PayRateType.Percentage && (
							<div className="flex justify-between text-sm text-gray-600 mb-2">
								<span>Gross Amount</span>
								<span>${payoutRequest.quantity}</span>
							</div>
						)}
						{(event.data?.opportunity?.payRate?.type === PayRateType.Hourly ||
							event.data?.opportunity?.payRate?.type === PayRateType.PerUnit) && (
							<div className="flex justify-between text-sm text-gray-600 mb-2">
								<span>
									{event.data?.opportunity?.payRate?.type === PayRateType.Hourly
										? "Hours Worked"
										: "Units Completed"}
								</span>
								<span>{payoutRequest.quantity}</span>
							</div>
						)}
						<div className="flex justify-between text-sm text-gray-600 mb-2 pb-2 border-b border-gray-300">
							<span>
								{getRateTypeDisplay(event.data?.opportunity?.payRate?.type)}
							</span>
							<span>
								{event.data?.opportunity?.payRate?.type === PayRateType.Percentage
									? `${event.data?.opportunity?.payRate?.value}%`
									: `$${event.data?.opportunity?.payRate?.value}`}
							</span>
						</div>
						<div className="flex justify-between font-semibold">
							<span>Total Payout</span>
							<span className="text-xl">${payoutRequest.amount ?? 0}</span>
						</div>
					</div>

					{/* References */}
					{evidence?.references && evidence.references.length > 0 && (
						<div className="mb-4">
							{evidence.references.map((ref) => (
								<div key={ref.id}>
									<h4 className="font-medium text-gray-900 mb-2">{ref.label}</h4>
									<p className="text-sm text-gray-700">{ref.referenceValue}</p>
								</div>
							))}
						</div>
					)}

					{/* Notes */}
					{evidence?.text && (
						<div className="mb-4">
							<h4 className="font-medium text-gray-900 mb-2">Notes</h4>
							<p className="text-sm text-gray-700 whitespace-pre-line">
								{evidence.text}
							</p>
						</div>
					)}

					{/* GPS Verification */}
					<GpsVerificationSection
						gpsCheckins={gpsCheckins}
						addressCoords={addressCoords}
						proximityThresholdFeet={proximityThresholdFeet}
						isOverlayOpen={isOverlayOpen}
					/>

					{/* Photo Evidence */}
					<PhotoGallery documents={documents} />
				</>
			)}
		</div>
	);
}

function Timeline({
	events,
	addressCoords,
	proximityThresholdFeet,
	isOverlayOpen = false,
}: {
	events: TimelineEvent[];
	addressCoords: [number, number] | null;
	proximityThresholdFeet: number;
	isOverlayOpen?: boolean;
}) {
	// Track which submission events are expanded, keyed by the event's unique id
	// (payout request ids and timestamps can repeat across submissions).
	const [expandedSubmissions, setExpandedSubmissions] = useState<Set<string>>(
		new Set(),
	);

	// The most recent submission with data (events are sorted newest-first).
	const latestSubmissionId = useMemo(() => {
		const latest = events.find((e) => e.type === "submitted" && e.data);
		return latest?.id ?? null;
	}, [events]);

	// Auto-expand that submission whenever it changes — initial load, evidence
	// arriving, or navigating to another claim. Guarded so manually collapsing it
	// isn't undone on the next render.
	const autoExpandedRef = useRef<string | null>(null);
	useEffect(() => {
		if (
			latestSubmissionId !== null &&
			autoExpandedRef.current !== latestSubmissionId
		) {
			autoExpandedRef.current = latestSubmissionId;
			setExpandedSubmissions((prev) => new Set(prev).add(latestSubmissionId));
		}
	}, [latestSubmissionId]);

	// Track lazy-loaded evidence by payoutRequestId
	const [loadedEvidence, setLoadedEvidence] = useState<
		Map<string, PayoutRequestEvidenceDto>
	>(new Map());
	// Track which payout requests are currently loading evidence
	const [loadingEvidence, setLoadingEvidence] = useState<Set<string>>(
		new Set(),
	);

	const toggleExpanded = useCallback(
		async (event: TimelineEvent) => {
			const isCurrentlyExpanded = expandedSubmissions.has(event.id);

			if (isCurrentlyExpanded) {
				// Collapsing - just remove from expanded set
				setExpandedSubmissions((prev) => {
					const next = new Set(prev);
					next.delete(event.id);
					return next;
				});
				return;
			}

			// Expand immediately
			setExpandedSubmissions((prev) => new Set(prev).add(event.id));

			// Lazy load evidence (keyed by payout request id) if needed
			const payoutRequestId = event.payoutRequestId;
			const hasEvidence = event.data?.evidence != null;
			const hasLoadedEvidence =
				payoutRequestId && loadedEvidence.has(payoutRequestId);
			const isLoading = payoutRequestId && loadingEvidence.has(payoutRequestId);

			// If we need to fetch evidence and aren't already loading
			if (payoutRequestId && !hasEvidence && !hasLoadedEvidence && !isLoading) {
				setLoadingEvidence((prev) => new Set(prev).add(payoutRequestId));
				try {
					const response = await getPayoutRequestEvidence(payoutRequestId);
					if (response.data) {
						setLoadedEvidence((prev) =>
							new Map(prev).set(payoutRequestId, response.data),
						);
					}
				} catch (error) {
					console.error("Failed to load evidence:", error);
				} finally {
					setLoadingEvidence((prev) => {
						const next = new Set(prev);
						next.delete(payoutRequestId);
						return next;
					});
				}
			}
		},
		[expandedSubmissions, loadedEvidence, loadingEvidence],
	);

	// Group events by date
	const groupedEvents = useMemo(() => {
		const groups: { date: string; events: TimelineEvent[] }[] = [];
		let currentDateStr = "";

		for (const event of events) {
			const dateStr = formatLocalDate(event.date, "weekdayDate");
			if (dateStr !== currentDateStr) {
				groups.push({ date: dateStr, events: [event] });
				currentDateStr = dateStr;
			} else {
				groups[groups.length - 1].events.push(event);
			}
		}

		return groups;
	}, [events]);

	return (
		<div className="space-y-4">
			{groupedEvents.map((group) => (
				<div key={group.date}>
					{/* Date pill */}
					<div className="flex justify-center mb-3">
						<Badge type="pill-color" color="gray-blue" size="md">
							{group.date}
						</Badge>
					</div>

					{/* Events for this date */}
					<div className="space-y-2">
						{group.events.map((event) => {
							const isSubmission = event.type === "submitted";
							const hasData = !!event.data?.payoutRequest;
							const canExpand = isSubmission && hasData;
							const prId = event.payoutRequestId;
							const isExpanded =
								canExpand && expandedSubmissions.has(event.id);
							const lazyEvidence = prId ? loadedEvidence.get(prId) : undefined;
							const isLoading = prId ? loadingEvidence.has(prId) : false;

							return (
								<div key={event.id}>
									{isSubmission ? (
										<ExpandedSubmissionEvent
											event={event}
											isExpanded={isExpanded}
											onCollapse={() => toggleExpanded(event)}
											lazyEvidence={lazyEvidence}
											isLoading={isLoading}
											addressCoords={addressCoords}
											proximityThresholdFeet={proximityThresholdFeet}
											isOverlayOpen={isOverlayOpen}
										/>
									) : (
										<CollapsedTimelineEvent
											event={event}
											showChevron={canExpand}
											onClick={
												canExpand ? () => toggleExpanded(event) : undefined
											}
										/>
									)}
								</div>
							);
						})}
					</div>
				</div>
			))}
		</div>
	);
}

function OpportunitySourceCard({ opportunity }: { opportunity: OpportunityDto }) {
	const addressString = formatAddress(opportunity.address);

	return (
		<div className="bg-white rounded-xl border border-gray-200 p-6">
			<h2 className="text-lg font-semibold text-gray-900 mb-4">Opportunity</h2>

			<Link
				to="/opps/$oppId"
				params={{ oppId: opportunity.id }}
				className="text-base font-semibold text-blue-600 hover:underline"
			>
				{opportunity.title}
			</Link>

			{opportunity.description && (
				<p className="mt-2 text-sm text-gray-600 whitespace-pre-wrap">
					{opportunity.description}
				</p>
			)}

			{addressString && (
				<div className="mt-4 flex items-center text-sm text-gray-600">
					<MarkerPin01 className="h-4 w-4 mr-2 shrink-0" />
					<span>{addressString}</span>
				</div>
			)}

			{opportunity.address && (
				<div className="mt-3">
					<AddressMap address={opportunity.address} />
				</div>
			)}

			{opportunity.url && (
				<a
					href={opportunity.url}
					target="_blank"
					rel="noopener noreferrer"
					className="mt-3 block text-sm text-blue-600 hover:underline"
				>
					{opportunity.url}
				</a>
			)}
		</div>
	);
}

function RouteComponent() {
	const { opportunity, proximityThresholdFeet, addressCoords } =
		Route.useLoaderData();
	const router = useRouter();
	const { claimId } = Route.useParams();
	const queryClient = useQueryClient();
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const {
		advanceToNext,
		goToNext,
		goToPrevious,
		returnToList,
		hasPrevious,
		hasNext,
	} = usePayoutReviewQueue({ opportunityClaimId: claimId });

	// Use query hooks for reactive data that updates when invalidated
	const { data: claimDetailData, refetch: refetchClaimDetail } =
		useGetClaimTimelineDetail(claimId);
	const { data: timelineData, refetch: refetchTimeline } =
		useGetClaimTimeline(claimId);

	const payoutRequest = claimDetailData?.data.currentPayoutRequest;
	const claim = claimDetailData?.data.claim;
	const payoutRequestHistory = claimDetailData?.data.payoutRequestHistory ?? [];
	const timelineEntries = timelineData?.data ?? [];

	const invalidateTimeline = useCallback(() => {
		refetchClaimDetail();
		refetchTimeline();
		queryClient.invalidateQueries({ queryKey: ["/api/payout-requests"], exact: false });
		queryClient.invalidateQueries({ queryKey: ["/api/payouts/payables"], exact: false });
	}, [refetchClaimDetail, refetchTimeline, queryClient]);

	const handleReviewed = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ["payout-requests"] });
		advanceToNext();
	}, [queryClient, advanceToNext]);

	const invalidateOpportunity = useCallback(() => {
		queryClient.invalidateQueries({
			queryKey: getGetOpportunityByIdQueryKey(opportunity.id),
		});
		router.invalidate();
	}, [queryClient, opportunity.id, router]);

	useDataRefresh(DataRefreshEvent.AddressGeocodeCompleted, invalidateOpportunity);
	useDataRefresh(DataRefreshEvent.PayoutRequestApproved, invalidateTimeline);
	useDataRefresh(DataRefreshEvent.PayoutRequestDenied, invalidateTimeline);
	useDataRefresh(DataRefreshEvent.PayoutRequestReturned, invalidateTimeline);
	useDataRefresh(DataRefreshEvent.PayoutRequestSubmitted, invalidateTimeline);
	useDataRefresh(DataRefreshEvent.PayoutRequestPaid, invalidateTimeline);
	useDataRefresh(DataRefreshEvent.OpportunityClaimed, invalidateTimeline);
	useDataRefresh(DataRefreshEvent.GpsCaptured, invalidateTimeline);
	useDataRefresh(DataRefreshEvent.GpsDeleted, invalidateTimeline);

	useHotkeys(
		["j", "down"],
		() => {
			if (!isOverlayOpen()) {
				goToNext();
			}
		},
		[goToNext],
	);
	useHotkeys(
		["k", "up"],
		() => {
			if (!isOverlayOpen()) {
				goToPrevious();
			}
		},
		[goToPrevious],
	);
	usePayoutReviewEscape(returnToList);

	const timelineEvents = useMemo(
		() =>
			claim
				? buildTimelineEvents(
						claim,
						timelineEntries,
						payoutRequest,
						payoutRequestHistory,
						opportunity,
					)
				: [],
		[claim, timelineEntries, payoutRequest, payoutRequestHistory, opportunity],
	);

	if (!claim || !payoutRequest) {
		return <PageLoading />;
	}

	return (
		<AdminLayout>
			<AdminPageHeader
				title="Payout Request"
				parent={{
					url: router.buildLocation({
						to: "/payout-requests/categories/$category",
						params: { category: "review" },
					}).href,
					label: "Pending Pay",
				}}
				titleActions={
					<>
						<PayoutQueueNav
							onPrevious={goToPrevious}
							onNext={goToNext}
							hasPrevious={hasPrevious}
							hasNext={hasNext}
						/>
						<PayoutShortcutsHelp shortcuts={payoutRequest ? payoutRequestShortcuts(payoutRequest.canBeReturned) : undefined} />
					</>
				}
			/>
			<PageContentContainer className="space-y-6">
				<OpportunitySourceCard opportunity={opportunity} />
				<Timeline
					events={timelineEvents}
					addressCoords={addressCoords}
					proximityThresholdFeet={proximityThresholdFeet}
					isOverlayOpen={isDrawerOpen}
				/>
			</PageContentContainer>
			{payoutRequest && (
				<PayoutRequestActions
					payoutRequestId={payoutRequest.id}
					name={payoutRequest.payeeName}
					amount={payoutRequest.amount ?? null}
					status={payoutRequest.status}
					concurrencyToken={payoutRequest.concurrencyToken}
					canBeReturned={payoutRequest.canBeReturned}
					onSuccess={handleReviewed}
					isDrawerOpen={isDrawerOpen}
					onDrawerOpenChange={setIsDrawerOpen}
				/>
			)}
		</AdminLayout>
	);
}
